import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseURL } from "../../services/api";
import AuthContext from '../../components/shared/AuthContext';
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import OrgContext from "../../organisation/OrgContext";
// import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
} from '@mui/x-data-grid';


import { LinearProgress } from "@mui/material";
import { toast } from "react-toastify";
import jwtInterceoptor from '../../components/shared/jwtInterceptor';


const CustomToolbar = () => {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
        </GridToolbarContainer>
    );
};

const Depots = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { organisation } = useContext(OrgContext);
    const { user } = useContext(AuthContext);

    const [depots, setDepots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDepotId, setSelectedDepotId] = useState(null);
    const [displayEdit, setDisplayEdit] = useState('none');
    const [showModal, setShowModal] = useState(false);
    const [newDepotName, setNewDepotName] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editDepotId, setEditDepotId] = useState(null);

    const getDepots = async () => {
        setLoading(true);
        try {
            const response = await jwtInterceoptor.get(`${baseURL}/depots/${organisation.id}`);
            setDepots(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la récupération des dépôts :", error);
            setLoading(false);
        }
    };

    const deleteDepot = async (depotId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Cette action est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.delete(`${baseURL}/depots/delete/${depotId}`);
                    if (response.status === 200) {
                        getDepots();
                        Swal.fire({
                            icon: 'success',
                            title: 'Supprimé !',
                            text: 'Le dépôt a été supprimé.',
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Échec !',
                            text: response.data.message || 'Une erreur est survenue, veuillez réessayer.',
                        });
                    }
                } catch (error) {
                    console.error('Erreur lors de la suppression :', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Erreur !',
                        text: 'Une erreur est survenue lors de la suppression du dépôt. Veuillez réessayer.',
                    });
                }
            }
        });
    };

    const createDepot = async () => {
        if (!newDepotName.trim()) {
            toast.error('Le nom du dépôt est requis.');
            return;
        }

        try {
            const response = await axios.post(`${baseURL}/depots/add`, {
                organisation_id: organisation.id,
                name: newDepotName,
                user_id: user.id
            });

            if (response.status === 201) {
                toast.success('Dépôt a été créé avec succès.');
                setShowModal(false);
                setNewDepotName('');
                getDepots(); // Rafraîchir la liste des dépôts
            }
        } catch (error) {
            console.error('Erreur lors de la création du dépôt :', error);
            toast.error('Une erreur est survenue lors de la création du dépôt.');
        }
    };

    const updateDepot = async () => {
        if (!newDepotName.trim()) {
            toast.error('Le nom du dépôt est requis.');
            return;
        }

        try {
            const response = await axios.patch(`${baseURL}/depots/update/${editDepotId}`, {
                name: newDepotName,
            });

            if (response.status === 200) {
                toast.success('Dépôt mis à jour avec succès.');
                setShowModal(false);
                setNewDepotName('');
                setEditDepotId(null);
                setIsEditMode(false);
                getDepots(); // Rafraîchir la liste des dépôts
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du dépôt :', error);
            toast.error('Une erreur est survenue lors de la mise à jour du dépôt.');
        }
    };

    const columns = [
        { field: 'name', headerName: 'Nom du dépôt', flex: 1 },
        { field: 'organisation_id', headerName: 'ID de l\'organisation', flex: 1 },
        { field: 'total_products', headerName: 'Quantité totale', flex: 1 },
        { 
            field: 'product_count', 
            headerName: 'Nombre de produits', 
            flex: 1,
            valueGetter: (params) => params.row.products?.length || 0 // Calculer le nombre de produits
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            disableClickEventBubbling: true,
            renderCell: (params) => {
                const onDelete = () => deleteDepot(params.row.id);
                const onEditClick = () => onEdit(params.row);
                return (
                    <div className="btn-list">
                        <button onClick={onEditClick} className="btn btn-sm btn-primary">
                            <span className="fe fe-edit"></span>
                        </button>
                        <button onClick={onDelete} className="btn btn-sm btn-danger">
                            <span className="fe fe-trash-2"></span>
                        </button>
                    </div>
                );
            },
        }
    ];

    useEffect(() => {
        getDepots();
    }, []);

    const closeModal = () => {
        setShowModal(false);
        setNewDepotName('');
        setEditDepotId(null);
        setIsEditMode(false);
    };

    const onEdit = (depot) => {
        setIsEditMode(true);
        setEditDepotId(depot.id);
        setNewDepotName(depot.name); // Pré-remplir le champ avec le nom existant
        setShowModal(true);
    };

    return (
        <div className="main-container container-fluid">
            <div className="row">
                <div className="col-md-12 col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Liste des dépôts</h3>
                            <div className="actionCardContainer">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(true)}
                                    className="floatRightBtn btn btn-icon btn-primary me-2 bradius"
                                >
                                    <i className="fe fe-plus"></i>
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <div style={{ height: 600, width: '100%' }}>
                                    <DataGrid
                                        rows={depots}
                                        columns={columns}
                                        pagination={false}
                                        checkboxSelection
                                        onRowSelectionModelChange={(ids) => {
                                            if (ids.length === 0) {
                                                setDisplayEdit('none');
                                            } else {
                                                setDisplayEdit('block');
                                                setSelectedDepotId(ids[0]);
                                            }
                                        }}
                                        // slots={{
                                        //     toolbar: GridToolbar,
                                        //     loadingOverlay: LinearProgress,
                                        // }}

                                        slots={{
                                            toolbar: CustomToolbar,
                                            loadingOverlay: LinearProgress,
                                        }}

                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {showModal && (
    <>
        <div
            className="offcanvas-backdrop fade show"
            onClick={closeModal}
        ></div>

        <div
            className="offcanvas offcanvas-bottom addProductCanvas show"
            tabIndex="-1"
            id="depotOffcanvasBottom"
            aria-labelledby="depotOffcanvasBottomLabel"
            style={{ visibility: 'visible' }}
        >
            <div className="offcanvas-header">
                <h5
                    className="offcanvas-title"
                    id="depotOffcanvasBottomLabel"
                >
                    {isEditMode ? 'Modifier le dépôt' : 'Créer un dépôt'}
                </h5>

                <button
                    type="button"
                    className="btn-close text-reset"
                    onClick={closeModal}
                    aria-label="Close"
                >
                    <i className="fe fe-x fs-18"></i>
                </button>
            </div>

            <div className="offcanvas-body">
                <div className="form-group">
                    <label>Nom du dépôt</label>

                    <input
                        type="text"
                        className="form-control"
                        value={newDepotName}
                        onChange={(e) => setNewDepotName(e.target.value)}
                    />
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={closeModal}
                    >
                        Annuler
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={isEditMode ? updateDepot : createDepot}
                    >
                        {isEditMode ? 'Modifier' : 'Créer'}
                    </button>
                </div>
            </div>
        </div>
    </>
)}
        </div>
    );
};

export default Depots;
